import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Paperclip } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { takeRight } from 'es-toolkit';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { useState, useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useQuill } from 'react-quilljs';

import './components/QuillTheme.css';
import './RichMessageEditor.css';
import { AttachedFileList } from './components/AttachedFileList';
import { AttachFilesPlaceholder } from './components/AttachFilesPlaceholder';

type Props = {
    readonly onAddAttachment: Function;
    readonly onDeleteAttachment: Function;
    readonly onSendMessage: Function;
    readonly testid?: string;
};

const RichMessageEditorComponent = ({
    onAddAttachment,
    onDeleteAttachment,
    onSendMessage,
    testid = 'messengerMessageEditor',
}: Props) => {
    const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [editorIsEmpty, setEditorIsEmpty] = useState(true);

    const onDrop = useCallback(
        (acceptedFiles: any[]) => {
            setLoading(true);
            const attachedFileNames = attachedFiles.map((attachedFile) => attachedFile.name);
            const filteredAcceptedFiles = acceptedFiles.filter((acceptedFile) => {
                return !attachedFileNames.includes(acceptedFile.name);
            });
            const filteredAcceptedFilesSize = filteredAcceptedFiles.reduce((a, b) => a + b.size, 0);

            if (filteredAcceptedFilesSize > 10000000) {
                setHasError(true);
                setErrorMessage('Your uploaded file size has exceeded 10MB.');
                setLoading(false);
                return () => [];
            } else if (filteredAcceptedFiles.length < acceptedFiles.length) {
                setHasError(true);
                setErrorMessage('You cannot upload multiple files with the same file name.');
                setLoading(false);
                return () => [];
            } else if (attachedFiles.length + filteredAcceptedFiles.length > 5) {
                setHasError(true);
                setErrorMessage('Your total files have exceeded 5.');
                setLoading(false);
                return () => [];
            } else {
                setHasError(false);
                setErrorMessage('');

                let lastFive: any[] = [];
                const fileList = [
                    ...attachedFiles,
                    ...filteredAcceptedFiles.map((newFile) =>
                        Object.assign(newFile, {
                            preview: URL.createObjectURL(newFile),
                        })
                    ),
                ];
                lastFive = takeRight(fileList, 5);

                onAddAttachment('', lastFive).then(({ error }: { error: any }) => {
                    setLoading(false);
                    if (!error) {
                        hasError && setHasError(false);
                        setAttachedFiles(lastFive);
                    } else {
                        setHasError(true);
                    }
                });
            }
        },
        [attachedFiles, onAddAttachment, hasError]
    );

    const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
        setHasError(true);
        setErrorMessage(
            `The following file(s) exceeded the maximum 10MB: ${rejectedFiles
                .map((item) => {
                    return item.file.name;
                })
                .join(', ')}`
        );
    }, []);

    const {
        getInputProps,
        getRootProps,
        isDragActive,
        open: openFileDialog,
    } = useDropzone({
        accept: 'image/*,.pdf,.doc,.docx,.csv,.zip,.xls,.xlsx,.numbers,.pages,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        maxSize: 10e6,
        noClick: true,
        onDrop,
        onDropRejected,
    });

    const { quill, Quill, quillRef } = useQuill({
        modules: {
            magicUrl: true,
            toolbar: [['bold', 'italic', 'underline', 'link', { list: 'ordered' }, { list: 'bullet' }]],
        },
        placeholder: 'Write a message',
        theme: 'snow',
    });

    if (!quill && Quill) {
        const MagicUrl = require('quill-magic-url').default;
        Quill.register('modules/magicUrl', MagicUrl);
    }

    if (quill) {
        quill.on('text-change', () => {
            const isEmpty = quill.getLength() === 1;
            if (isEmpty && !editorIsEmpty) {
                setEditorIsEmpty(true);
            } else if (!isEmpty && editorIsEmpty) {
                setEditorIsEmpty(false);
            }
        });

        quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
            let ops: any[] = [];
            delta.ops.forEach((op) => {
                if (op.insert && typeof op.insert === 'string') {
                    ops.push({
                        insert: op.insert,
                    });
                }
            });
            delta.ops = ops;
            return delta;
        });
    }

    const handleSendMessage = () => {
        setLoading(true);
        if (quill) {
            const { ops: deltaOps } = quill.getContents();
            const config = { inlineStyles: true };
            const deltaToHTML = new QuillDeltaToHtmlConverter(deltaOps, config);
            const messageHTML = deltaToHTML.convert();
            onSendMessage(messageHTML).then(({ error }: { error: any }) => {
                setLoading(false);
                if (!error) {
                    setAttachedFiles([]);
                    hasError && setHasError(false);
                    // @ts-expect-error can't set empty array to a typed array
                    quill.setContents([]);
                } else {
                    setHasError(true);
                }
            });
        }
    };

    const handleDeleteDraft = (fileName: string) => {
        setLoading(true);

        onDeleteAttachment(fileName).then(({ error }: { error: any }) => {
            setLoading(false);
            if (!error) {
                hasError && setHasError(false);
            } else {
                setHasError(true);
            }
        });
    };

    const disabled = loading || attachedFiles.length >= 5;

    return (
        <>
            <div
                className="relative focus:outline-hidden"
                {...getRootProps()}
                data-testid={testid}
                // @ts-ignore disabled not a prop?
                disabled={disabled}
            >
                <input {...getInputProps()} />
                <div className="messenger-editor border border-border-secondary">
                    <div ref={quillRef} />
                    <InlineButton
                        className="ml-[20px] pt-xs"
                        disabled={disabled}
                        icon={Paperclip}
                        iconPlacement="left"
                        onClick={openFileDialog}
                        size="sm"
                        style="neutral"
                    >
                        Browse Files
                    </InlineButton>
                    {attachedFiles.length ? (
                        <AttachedFileList
                            attachedFiles={attachedFiles}
                            handleDeleteDraft={handleDeleteDraft}
                            loading={loading}
                            setAttachedFiles={setAttachedFiles}
                        />
                    ) : (
                        <AttachFilesPlaceholder />
                    )}
                </div>
                <div
                    className={cn(
                        'absolute top-0 bottom-0 flex w-full items-center justify-center',
                        'rounded-[2px] border-4 border-dashed border-primary-500 outline-hidden',
                        isDragActive ? 'z-[999] bg-[#e3e3e373]' : 'invisible bg-transparent'
                    )}
                >
                    <H2>Drop files here...</H2>
                </div>
            </div>
            {hasError && (
                <BodySecondary color="critical">
                    {errorMessage || 'An Error has occurred, please try again later.'}
                </BodySecondary>
            )}
            <div className="my-md flex justify-end space-x-sm">
                <Button
                    aria-label="Send message"
                    className="w-[254px]"
                    disabled={editorIsEmpty && attachedFiles.length === 0}
                    loading={loading}
                    onClick={handleSendMessage}
                >
                    Send
                </Button>
            </div>
        </>
    );
};

export const RichMessageEditor = RichMessageEditorComponent;
